/* eslint-disable max-len */
import {DelayParameter} from '@frontapp/workflows/rules/parameters';
import React, {FC} from 'react';
import styled, {useTheme} from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {TeamModel} from '../../../../libs/shared/core/src/models/api/teamModel';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import FireFilledSmall from '../../../assets/images/fireFilledSmall.svg';
import Flourish from '../../../assets/images/flourish.svg';
import Kendal from '../../../assets/images/kendal.svg';
import Sam from '../../../assets/images/sam.svg';
import Shahine from '../../../assets/images/shahine.svg';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {getFirstTeamUserIsAdminOf} from '../../../store/data/teams/teamsSelectors';
import {OnboardingInboxInviteTeammatesVisual} from './onboardingInboxInviteTeammatesVisual';
import {OnboardingTeammateGroupForAnimation} from './onboardingTeammateGroupForAnimation';
import {
  computeInboxAnimationStep,
  finalInboxAnimationStep,
  ILLUSTRATION_PANEL_WIDTH,
  INBOX_ANIMATION_DURATION_MS,
  InboxAnimationStep,
  OnboardingInboxIllustration,
  OnboardingInboxVisualFeature,
} from './workflows/onboardingInboxIllustration';

/*
 * Props.
 */

interface OnboardingInboxVisualProps {
  inboxName: string;
  circleName?: string;
  color: string | undefined;
  companyName: string;
  isCircleVisualVisible?: boolean;
  inboxAnimation?: boolean;
  feature?: OnboardingInboxVisualFeature;
  team?: Pick<TeamModel, 'name' | 'color'>;
  position: OnboardingInboxVisualInboxPosition | undefined;
  previousPosition: OnboardingInboxVisualInboxPosition | undefined;
  slaRuleSlaReplyTimeGoal?: DelayParameter;
  tagRuleTagName?: string;
  tagRuleKeywords?: ReadonlyArray<string>;
  invitedTeammateHandles?: ReadonlyArray<string>;
}

export enum OnboardingInboxVisualInboxPosition {
  FAR_LEFT = 'far_left',
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

enum PanAnimation {
  LEFT_CENTER = 'left_center',
  CENTER_RIGHT = 'center_right',
}

/*
 * Style.
 */

const StyledParentDiv = styled.div`
  // Because we set the z-index of the teammate group animation image.
  isolation: isolate;

  position: relative;
  width: ${ILLUSTRATION_PANEL_WIDTH}px;
  height: 692px;

  overflow: hidden;

  background: ${(p) => p.theme.greys.shade20};

  @keyframes diagonal {
    0% {
      transform: translatex(0px) translatey(0px);
    }
    100% {
      transform: translatex(-130px) translatey(80px) scale(0.7);
    }
  }
`;

const StyledTeammateGroupArrowDiv = styled.div`
  position: absolute;
  top: 425px;
  left: 245px;

  animation-name: opacityChange;
  animation-duration: 0.5s;
  animation-fill-mode: forwards;
`;

interface StyledInboxWrapperProps {
  $shouldShowCircleVisual: boolean;
  $inboxPosition: OnboardingInboxVisualInboxPosition | undefined;
  $panAnimation: PanAnimation | undefined;
}

const StyledInboxWrapper = styled.div<StyledInboxWrapperProps>`
  position: absolute;
  top: 113px;

  left: ${(p) => {
    if (p.$inboxPosition === OnboardingInboxVisualInboxPosition.RIGHT) {
      return '-210px';
    }

    if (p.$inboxPosition === OnboardingInboxVisualInboxPosition.CENTER) {
      return '100px';
    }

    if (p.$inboxPosition === OnboardingInboxVisualInboxPosition.LEFT) {
      return '264px';
    }

    return '288px';
  }};

  animation-name: ${(p) => p.$panAnimation ?? 'none'};
  animation-duration: 0.3s;
  animation-fill-mode: forwards;

  @keyframes ${PanAnimation.LEFT_CENTER} {
    0% {
      left: 264px;
    }
    100% {
      left: 100px;
    }
  }

  @keyframes ${PanAnimation.CENTER_RIGHT} {
    0% {
      left: 100px;
    }
    100% {
      left: -210px;
    }
  }
`;

/*
 * SLA rule style.
 */

const StyledSlaRuleArrowDiv = styled.div`
  position: absolute;
  top: 189px;
  left: 301px;
  transform: rotate(-8deg);

  animation-name: opacityChange;
  animation-duration: 0.6s;
  animation-fill-mode: forwards;

  @keyframes opacityChange {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const StyledSlaRuleDetailsDiv = styled.div`
  position: absolute;
  top: 157px;
  left: 316px;
  transform: rotate(-8deg);
  display: flex;
  flex-direction: column;

  animation-name: opacityChange;
  animation-duration: 0.6s;
  animation-fill-mode: forwards;

  @keyframes opacityChange {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const StyledSlaRuleBreachDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const StyledSlaRulePlusIcon = styled(Icon)`
  color: ${(p) => p.theme.palette.orange.shade50};
`;

const StyledSlaRuleBellIcon = styled(Icon)`
  color: ${(p) => p.theme.palette.orange.shade40};
`;

const StyledSlaRuleBreachPill = styled.div`
  width: 75px;
  height: 24px;
  padding: 4px 8px 4px 4px;
  border-radius: 4px;
  color: ${(p) => p.theme.palette.orange.shade50};
  background: ${(p) => p.theme.palette.orange.shade30};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  line-height: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const StyledSlaRuleNotifyPill = styled.div`
  margin-top: 6px;
  margin-left: 22px;
  width: 138px;
  height: 28px;
  padding: 6px 8px;
  border-radius: 20px;
  color: ${(p) => p.theme.palette.orange.shade50};
  background: ${(p) => p.theme.greys.white};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  line-height: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0px 0px 10px 0px rgba(0, 27, 56, 0.1);
`;

/*
 * Tag rule style.
 */

const StyledTagRuleArrowDiv = styled.div`
  position: absolute;
  top: 194px;
  left: 311px;
  transform: rotate(-6deg);

  animation-name: opacityChange;
  animation-duration: 0.6s;
  animation-fill-mode: forwards;

  @keyframes opacityChange {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const StyledTagRuleDetailsDiv = styled.div`
  position: relative;
  top: 116px;
  left: 313px;
  transform: rotate(-8deg);
  display: flex;
  flex-direction: row;
  align-items: center;

  animation-name: opacityChange;
  animation-duration: 0.6s;
  animation-fill-mode: forwards;

  @keyframes opacityChange {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const StyledTagRulePlusIcon = styled(Icon)`
  color: #006eb2;
`;

const StyledTagRulePlaceholder = styled.div`
  background: #006eb2;
  border-radius: 2px;
  width: 48px;
  height: 12px;
  margin: 0 2px;
`;

const StyledTagRulePill = styled.div`
  margin-left: 6px;
  min-width: 56px;
  max-width: 186px;
  height: 24px;
  padding: 0 6px;
  gap: 6px;
  border-radius: 4px;
  color: #006eb2;
  background: #b0f1fc;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  line-height: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const StyledTagRulePillTagName = styled.div`
  ${ellipsis()};
`;

/*
 * Assign rule style.
 */

const StyledAssignRuleArrowDiv = styled.div`
  position: absolute;
  top: 158px;
  left: 91px;
  transform: rotate(-6deg);

  animation-name: opacityChange;
  animation-duration: 0.6s;
  animation-fill-mode: forwards;

  @keyframes opacityChange {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const StyledAssignRuleDetailsDiv = styled.div`
  position: relative;
  top: 101px;
  left: 115px;
  transform: rotate(-6deg);
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  animation-name: opacityChange;
  animation-duration: 0.6s;
  animation-fill-mode: forwards;

  @keyframes opacityChange {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const StyledAssignRuleTeammateGroupPill = styled.div`
  max-width: 200px;
  height: 26px;
  margin-bottom: 6px;
  padding: 0 6px;
  border-radius: 20px;
  color: ${(p) => p.theme.palette.blue.shade50};
  background: ${(p) => p.theme.greys.white};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  line-height: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 10px 0px rgba(0, 27, 56, 0.1);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const StyledAssignRuleTeammateGroupPillCircleName = styled.div`
  ${ellipsis()};
`;

const StyledAssignRuleTeammatesPill = styled.div`
  width: 68px;
  height: 26px;
  border-radius: 20px;
  color: ${(p) => p.theme.palette.blue.shade50};
  background: ${(p) => p.theme.greys.white};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  line-height: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  box-shadow: 0px 0px 10px 0px rgba(0, 27, 56, 0.1);
`;

/*
 * Created resources slide style (inboxAnimation).
 */

const StyledSharedInboxFlourishDiv = styled.div`
  position: absolute;
  top: 390px;
  left: 68px;

  opacity: 0;
  animation-name: inboxAnimationSharedInboxFlourish;
  animation-duration: ${INBOX_ANIMATION_DURATION_MS}ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  animation-direction: normal;

  @keyframes inboxAnimationSharedInboxFlourish {
    ${computeInboxAnimationStep(InboxAnimationStep.INITIAL_PAUSE)} {
      opacity: 0;
    }

    ${computeInboxAnimationStep(InboxAnimationStep.REVEAL_SHARED_INBOX)} {
      opacity: 1;
    }

    ${computeInboxAnimationStep(InboxAnimationStep.REVEAL_SHARED_INBOX_PAUSE)} {
      opacity: 1;
    }

    ${computeInboxAnimationStep(InboxAnimationStep.HIDE_SHARED_INBOX_FLOURISH)} {
      opacity: 0;
    }
  }
`;

const StyledAssignConversationFlourishDiv = styled.div`
  position: absolute;
  top: 162px;
  left: 470px;

  opacity: 0;
  animation-name: inboxAnimationAssignConversationFlourish;
  animation-duration: ${INBOX_ANIMATION_DURATION_MS}ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  animation-direction: normal;

  @keyframes inboxAnimationAssignConversationFlourish {
    ${computeInboxAnimationStep(InboxAnimationStep.HIDE_SHARED_INBOX_FLOURISH)} {
      opacity: 0;
    }

    ${computeInboxAnimationStep(InboxAnimationStep.ASSIGN_CONVERSATION)} {
      opacity: 1;
    }

    ${computeInboxAnimationStep(InboxAnimationStep.ASSIGN_CONVERSATION_PAUSE)} {
      opacity: 0;
    }

    ${computeInboxAnimationStep(InboxAnimationStep.HIDE_ASSIGNED_CONVERSATION)} {
      opacity: 0;
    }
  }
`;

const StyledTagConversationFlourishDiv = styled.div`
  position: absolute;
  top: 275px;
  left: 475px;

  opacity: 0;
  animation-name: inboxAnimationTagConversationFlourish;
  animation-duration: ${INBOX_ANIMATION_DURATION_MS}ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  animation-direction: normal;

  @keyframes inboxAnimationTagConversationFlourish {
    ${computeInboxAnimationStep(InboxAnimationStep.TAG_CONVERSATION_2)} {
      opacity: 0;
    }

    ${computeInboxAnimationStep(InboxAnimationStep.TAG_CONVERSATION_3)} {
      opacity: 1;
    }

    ${finalInboxAnimationStep()} {
      opacity: 1;
    }
  }
`;

/*
 * Invite teammates style.
 */

const StyledInviteTeammatesArrowDiv = styled.div`
  position: absolute;
  transform: scale(0.6);
  top: 395px;
  left: 256px;
  z-index: 1;
`;

const StyledOnboardingInboxInviteTeammatesVisualDiv = styled.div`
  position: absolute;
  top: 368px;
  left: 24px;
`;

/*
 * Component.
 */

export const OnboardingInboxVisual: FC<OnboardingInboxVisualProps> = ({
  inboxName,
  circleName,
  color,
  companyName,
  feature,
  isCircleVisualVisible,
  inboxAnimation,
  team,
  position,
  previousPosition,
  slaRuleSlaReplyTimeGoal,
  tagRuleTagName,
  tagRuleKeywords,
  invitedTeammateHandles,
}) => {
  const theme = useTheme();
  const defaultTeam = useAppSelector(getFirstTeamUserIsAdminOf);
  const shouldShowCircleVisual = isCircleVisualVisible ?? true;

  return (
    <StyledParentDiv>
      <StyledInboxWrapper
        $shouldShowCircleVisual={shouldShowCircleVisual}
        $inboxPosition={position}
        $panAnimation={mapPositionToPanAnimation(previousPosition, position)}
      >
        <OnboardingInboxIllustration
          companyName={companyName}
          team={team ?? defaultTeam ?? {name: companyName, color: PaletteColorsEnum.RED}}
          inbox={{name: inboxName, color: color ?? theme.greys.shade50}}
          feature={feature}
          inboxAnimation={inboxAnimation}
          slaRuleSlaReplyTimeGoal={slaRuleSlaReplyTimeGoal}
          tagRuleKeywords={tagRuleKeywords}
        />
      </StyledInboxWrapper>

      {/* Inbox animation has three components: teammate group image, inbox image, and arrow. */}
      {shouldShowCircleVisual && <OnboardingTeammateGroupForAnimation name={circleName ?? ''} />}
      {shouldShowCircleVisual && (
        <StyledTeammateGroupArrowDiv>
          <svg xmlns="http://www.w3.org/2000/svg" width={57} height={12} fill="none">
            <path
              fill="#001B38"
              d="M55.12 8.155c-1.265.992-5.184 2.896-7.191 3.515-2.75.8-3.716.077-2.537-1.965l.866-1.5-2.789-.054c-1.542-.002-3.052-.151-3.371-.336-.32-.184-.863-.165-1.19.035-.353.241-2.071.36-3.821.35-1.71.012-3.924.068-4.85.2-.968.107-1.966.086-2.23-.01-.303-.12-1.262-.118-2.165-.028-.84.07-3.061.232-4.883.347-8.207.483-8.919.517-12.292 1.014-6.234.845-9.134.615-8.316-.802C.856 8.046 11 5.96 14.244 6.054c.727.031 3.868-.155 6.993-.407 3.084-.274 5.841-.349 6.04-.234.24.139.824.142 1.295-.03a14.241 14.241 0 0 1 2.438-.26c.895.017 2.085-.017 2.693-.055 3.452-.174 6.057-.17 11.17.005 3.844.163 5.498.063 5.011-.385C47.89 2.981 47.07 1.73 47.503.98c.433-.75 2.359-1.138 2.517-.49.032.129 1.428 1.213 3.072 2.328 2.338 1.572 3.056 2.265 2.999 3.01-.01 1.215-.22 1.761-.972 2.327Z"
            />
          </svg>
        </StyledTeammateGroupArrowDiv>
      )}
      {feature === OnboardingInboxVisualFeature.SLA_RULE && (
        <>
          <StyledSlaRuleArrowDiv>
            <svg width="16" height="30" viewBox="0 0 16 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15.8425 1.6086C15.7819 1.67001 15.7447 2.51664 15.7481 3.47765C15.8099 5.71722 15.5987 6.39816 14.7281 6.47269C13.9867 6.54048 13.766 6.17334 13.5006 4.26479L13.3274 3.19612L12.3507 4.06262C11.8019 4.55727 11.35 5.04687 11.3562 5.13393C11.3645 5.25001 10.9773 5.73623 10.4608 6.2292C9.97656 6.72049 9.55703 7.20841 9.56321 7.29546C9.56939 7.38252 9.30529 7.77495 8.94215 8.14341C8.61133 8.51019 7.73234 9.83762 6.97646 11.0713C6.25291 12.3032 5.48878 13.4208 5.30309 13.5469C5.14972 13.6715 5.09332 13.7909 5.25493 13.7825C5.38423 13.7757 4.96947 14.7877 4.37521 16.0129C3.75068 17.2688 3.24308 18.3438 3.21488 18.4036C3.22106 18.4906 3.23549 18.6938 3.24786 18.8679C3.25817 19.013 3.133 19.0778 2.93288 19.0008C2.73275 18.9239 2.70868 19.0416 2.8242 19.2977C2.96998 19.5232 2.90187 19.9345 2.6921 20.1784C2.48233 20.4224 2.3998 20.6306 2.49677 20.6255C2.59373 20.6205 2.48572 21.3834 2.25872 22.2982C2.04202 23.3581 1.96971 25.0803 2.12573 26.8198C2.23705 28.3869 2.23491 29.7268 2.07948 29.8223C1.55266 30.1702 0.598136 29.5209 0.376737 28.6878C-0.0722467 26.9345 -0.0885035 22.5954 0.362037 21.1738C0.595223 20.3461 0.826348 19.4893 0.842179 19.2555C0.890333 19.0199 0.938487 18.7844 0.966688 18.7247C1.02927 18.6923 1.05335 18.5745 1.10769 18.4261C1.13177 18.3083 1.32298 17.8032 1.51626 17.3271C1.70748 16.8219 1.90281 16.3748 1.92895 16.2861C1.92277 16.199 2.09197 15.8407 2.29143 15.4516C2.49296 15.0916 2.56931 14.7963 2.44002 14.8031C2.34305 14.8081 2.39326 14.6016 2.60303 14.3577C2.81074 14.0847 3.30251 13.2435 3.66911 12.4671C4.62998 10.4654 5.03509 9.7744 5.75312 8.92139C6.08188 8.5256 6.37213 8.04443 6.36182 7.89933C6.34945 7.72521 6.56128 7.51027 6.81368 7.40973C7.06402 7.28017 7.27585 7.06522 7.26348 6.89111C7.25318 6.74601 7.51522 6.32457 7.84604 5.95779C8.17686 5.59101 8.86669 4.79773 9.37495 4.18868L10.2684 3.06439L9.03391 3.04139C7.96106 3.00996 7.7912 2.90231 7.64475 2.21089C7.54918 1.77897 7.58909 1.42737 7.74864 1.38992C7.87794 1.38317 9.10209 1.26108 10.4232 1.13393C11.8434 1.03073 13.247 0.695386 13.8103 0.403867C14.9974 -0.240582 15.8542 -0.0522535 15.9161 0.818332C15.9429 1.19559 15.903 1.54719 15.8425 1.6086Z"
                fill="#001B38"
              />
            </svg>
          </StyledSlaRuleArrowDiv>
          <StyledSlaRuleDetailsDiv>
            <StyledSlaRuleBreachDiv>
              <StyledSlaRulePlusIcon name="plusCircleFilledSmall" />
              <StyledSlaRuleBreachPill>
                <img src={FireFilledSmall} alt="Flame" />
                Breach
              </StyledSlaRuleBreachPill>
            </StyledSlaRuleBreachDiv>
            <StyledSlaRuleNotifyPill>
              <StyledSlaRuleBellIcon name="bellSmall" />
              Notify teammates
            </StyledSlaRuleNotifyPill>
          </StyledSlaRuleDetailsDiv>
        </>
      )}
      {feature === OnboardingInboxVisualFeature.TAG_RULE && (
        <>
          <StyledTagRuleArrowDiv>
            <svg width="24" height="59" viewBox="0 0 24 59" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M23.4351 4.88429C23.0139 5.36404 22.1477 5.08244 21.0715 4.1043C20.4417 3.53433 19.9063 3.09874 19.8816 3.10618C19.8322 3.12107 19.92 3.50166 20.0228 3.93201C20.2456 4.76038 20.3246 5.46927 20.2629 6.24771C20.2488 6.46906 20.3203 7.15307 20.4188 7.74757C20.5247 8.36694 20.553 9.4439 20.501 10.1651C20.3865 11.3937 20.292 14.9229 20.3313 15.5895C20.3697 15.985 20.156 17.8677 19.8272 20.0835C19.6677 21.1628 19.4769 22.4958 19.4315 22.9708C19.3689 23.4782 19.2591 23.9183 19.1604 23.9481C19.0369 23.9853 19.0303 24.2316 19.1128 24.5052C19.1953 24.7789 19.1887 25.0251 19.0652 25.0623C18.9664 25.0921 18.6715 26.3479 18.3947 27.8425C18.1179 29.3371 17.823 30.593 17.7242 30.6227C17.6254 30.6525 17.535 30.9783 17.5659 31.3489C17.5721 31.7269 17.4818 32.0527 17.3583 32.0899C17.2348 32.1272 17.024 32.6792 16.9346 33.276C16.8204 33.8803 16.6299 34.5891 16.4849 34.8228C16.372 35.0739 16.0828 35.8124 15.8526 36.4788C15.1644 38.396 13.9258 41.3474 13.4637 42.138C13.2402 42.5582 13.0929 42.874 13.167 42.8517C13.3151 42.807 11.028 47.1058 9.76203 49.2514C9.28271 50.0744 8.73591 50.6734 8.51583 50.6584C8.32046 50.6359 8.17978 50.7054 8.23447 50.7975C8.41576 51.0413 7.76009 52.3516 7.41434 52.4559C7.24146 52.508 7.12328 52.6522 7.15328 52.7517C7.19078 52.8761 6.82654 53.3658 6.35602 53.8604C5.8608 54.3625 5.43967 54.8423 5.38808 54.9393C5.22361 55.2873 4.33416 56.1797 2.62843 57.6709C1.5586 58.5904 0.45695 58.8683 0.276953 58.2713C0.216954 58.0722 0.253544 57.9255 0.327634 57.9032C0.409223 57.9057 0.825959 57.5901 1.22769 57.2248C1.63693 56.8843 2.06867 56.6184 2.18245 56.6384C2.32843 56.6758 2.38752 56.6037 2.34253 56.4545C2.26753 56.2057 3.58781 54.7764 4.02485 54.6175C4.14833 54.5803 4.35249 54.2745 4.49976 53.9587C4.64703 53.643 5.24013 52.84 5.77194 52.1912C7.32328 50.3666 7.45115 50.1652 7.36116 49.8667C7.31616 49.7175 7.49562 49.4191 7.77168 49.1731C8.2744 48.6958 10.126 45.7224 10.7688 44.2802C10.9946 43.778 11.6106 42.4254 12.1555 41.2841C13.2477 38.9195 14.5026 35.6646 14.305 35.7242C14.2309 35.7465 14.3879 35.3736 14.6383 34.8639C14.921 34.3716 15.2909 33.3645 15.4588 32.5812C15.659 31.8152 15.8966 31.1737 16.0201 31.1365C16.1436 31.0993 16.1974 30.9202 16.1449 30.7461C16.0999 30.5968 16.2475 29.6568 16.5187 28.6795C17.0578 26.5359 17.8936 22.159 17.7454 22.2037C17.696 22.2186 17.7952 21.5645 18.005 20.7414C18.1827 19.9009 18.3325 18.8788 18.2866 18.4584C18.2482 18.0629 18.3011 17.6128 18.3773 17.5084C18.4783 17.3965 18.4796 17.0433 18.3746 16.6951C18.2771 16.3717 18.2934 16.0682 18.4169 16.031C18.6145 15.9714 18.6307 15.668 18.5865 14.2702C18.6059 14.1558 18.6178 14.0165 18.62 13.9345C18.6222 13.8524 18.6341 13.7131 18.646 13.5738C18.6407 13.4669 18.6614 12.9993 18.7142 12.5492C18.7596 12.0742 18.775 11.4996 18.7494 11.236C18.7238 10.9723 18.7198 10.5122 18.7189 10.2411C18.7179 9.97 18.7139 9.50987 18.6883 9.24621C18.6627 8.98256 18.6375 8.09463 18.6348 7.28133C18.6156 5.25179 18.3495 3.29671 18.1025 3.37117C18.0037 3.40095 17.8855 3.54513 17.8414 3.66697C17.8221 3.78137 17.4397 4.03233 17.0027 4.19122C16.5731 4.375 16.1586 4.60853 16.1145 4.73037C16.0704 4.85221 15.7719 5.02364 15.4261 5.12788C14.9816 5.2619 14.739 5.1722 14.5621 4.7642C14.323 4.23925 14.3649 4.19949 16.0416 2.87988C16.3004 2.66615 16.7343 2.31822 17.0179 2.09705C17.2693 1.85844 17.9577 1.46093 18.5204 1.18274C19.5373 0.713313 19.6189 0.715853 20.8724 1.47773C22.5991 2.55826 23.9325 4.30013 23.4351 4.88429Z"
                fill="#001B38"
              />
            </svg>
          </StyledTagRuleArrowDiv>
          <StyledTagRuleDetailsDiv>
            <StyledTagRulePlusIcon name="plusCircleFilledSmall" />
            <StyledTagRulePill>
              <StyledTagRulePlusIcon name="tagFilledSmall" />
              {tagRuleTagName ? (
                <StyledTagRulePillTagName>{tagRuleTagName}</StyledTagRulePillTagName>
              ) : (
                <StyledTagRulePlaceholder />
              )}
            </StyledTagRulePill>
          </StyledTagRuleDetailsDiv>
        </>
      )}
      {feature === OnboardingInboxVisualFeature.ASSIGN_RULE && (
        <>
          <StyledAssignRuleArrowDiv>
            <svg width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M19.3631 3.33666C19.2994 3.36802 19.0415 4.01612 18.7842 4.76412C18.2264 6.52184 17.878 6.99408 17.181 6.8165C16.5863 6.66866 16.514 6.32353 16.824 4.76807L16.9784 3.89043L15.9848 4.29985C15.4244 4.53593 14.9406 4.79431 14.9219 4.86366C14.8969 4.95613 14.4644 5.22938 13.9295 5.47289C13.4202 5.72383 12.9621 5.98964 12.9433 6.059C12.9246 6.12835 12.6131 6.36197 12.2311 6.55018C11.8747 6.74582 10.8324 7.53999 9.91104 8.29453C9.01531 9.05651 8.11898 9.71858 7.94049 9.76645C7.78758 9.82175 7.71142 9.89935 7.83934 9.93652C7.94168 9.96625 7.34551 10.6407 6.55211 11.4324C5.72688 12.2398 5.04147 12.9383 5.00339 12.9771C4.98465 13.0464 4.94092 13.2082 4.90344 13.3469C4.8722 13.4625 4.75737 13.479 4.62261 13.3651C4.48785 13.2511 4.43728 13.3362 4.4578 13.5665C4.51016 13.7812 4.34595 14.0825 4.11688 14.2154C3.88782 14.3483 3.76733 14.4879 3.84409 14.5102C3.92084 14.5325 3.63049 15.0963 3.20657 15.7461C2.75141 16.5114 2.22931 17.8307 1.88006 19.2252C1.5427 20.4735 1.17857 21.5146 1.03191 21.5468C0.52827 21.6747 -0.038137 20.9118 0.0151173 20.2042C0.140368 18.7197 1.30147 15.3422 2.03627 14.3589C2.44145 13.7785 2.85287 13.175 2.92844 12.9975C3.02958 12.8274 3.13073 12.6574 3.16881 12.6186C3.22622 12.6103 3.27679 12.5253 3.3592 12.4246C3.40977 12.3395 3.69506 11.9986 3.9741 11.6807C4.2594 11.3398 4.53219 11.045 4.57652 10.9831C4.59526 10.9138 4.82373 10.681 5.08403 10.4325C5.33808 10.2071 5.47731 9.99822 5.37497 9.96849C5.29822 9.94619 5.39312 9.79924 5.62218 9.66633C5.8575 9.51031 6.46734 8.98939 6.96236 8.48497C8.25078 7.18885 8.75264 6.76122 9.54157 6.29233C9.90421 6.07357 10.26 5.77803 10.2912 5.66244C10.3287 5.52374 10.5515 5.41395 10.775 5.40406C11.0046 5.37106 11.2274 5.26127 11.2649 5.12257C11.2962 5.00698 11.6139 4.75024 11.9703 4.5546C12.3266 4.35896 13.0775 3.92887 13.6374 3.59289L14.636 2.96055L13.6826 2.60874C12.8571 2.29409 12.7541 2.16446 12.8273 1.58735C12.8699 1.22573 12.996 0.963187 13.1302 0.977235C13.2325 1.00697 14.2172 1.24319 15.2786 1.50171C16.4105 1.80565 17.5924 1.92466 18.1092 1.8504C19.2063 1.67051 19.8215 2.04871 19.6341 2.74223C19.5529 3.04275 19.4267 3.30529 19.3631 3.33666Z"
                fill="#001B38"
              />
            </svg>
          </StyledAssignRuleArrowDiv>
          <StyledAssignRuleDetailsDiv>
            <StyledAssignRuleTeammateGroupPill>
              <StyledAssignRuleTeammateGroupPillCircleName>
                {circleName || <Trans id="onboarding-inbox-visual-teammate-group">Teammate group</Trans>}
              </StyledAssignRuleTeammateGroupPillCircleName>
            </StyledAssignRuleTeammateGroupPill>
            <StyledAssignRuleTeammatesPill>
              <img src={Shahine} alt="Shahine" />
              <img src={Kendal} alt="Kendal" />
              <img src={Sam} alt="Sam" />
            </StyledAssignRuleTeammatesPill>
          </StyledAssignRuleDetailsDiv>
        </>
      )}
      {inboxAnimation && (
        <>
          <StyledSharedInboxFlourishDiv>
            <img src={Flourish} alt="Flourish" />
          </StyledSharedInboxFlourishDiv>
          <StyledAssignConversationFlourishDiv>
            <img src={Flourish} alt="Flourish" />
          </StyledAssignConversationFlourishDiv>
          <StyledTagConversationFlourishDiv>
            <img src={Flourish} alt="Flourish" />
          </StyledTagConversationFlourishDiv>
        </>
      )}
      {feature === OnboardingInboxVisualFeature.INVITE_TEAMMATES && (
        <>
          <StyledInviteTeammatesArrowDiv>
            <svg xmlns="http://www.w3.org/2000/svg" width={57} height={12} fill="none">
              <path
                fill="#001B38"
                d="M55.12 8.155c-1.265.992-5.184 2.896-7.191 3.515-2.75.8-3.716.077-2.537-1.965l.866-1.5-2.789-.054c-1.542-.002-3.052-.151-3.371-.336-.32-.184-.863-.165-1.19.035-.353.241-2.071.36-3.821.35-1.71.012-3.924.068-4.85.2-.968.107-1.966.086-2.23-.01-.303-.12-1.262-.118-2.165-.028-.84.07-3.061.232-4.883.347-8.207.483-8.919.517-12.292 1.014-6.234.845-9.134.615-8.316-.802C.856 8.046 11 5.96 14.244 6.054c.727.031 3.868-.155 6.993-.407 3.084-.274 5.841-.349 6.04-.234.24.139.824.142 1.295-.03a14.241 14.241 0 0 1 2.438-.26c.895.017 2.085-.017 2.693-.055 3.452-.174 6.057-.17 11.17.005 3.844.163 5.498.063 5.011-.385C47.89 2.981 47.07 1.73 47.503.98c.433-.75 2.359-1.138 2.517-.49.032.129 1.428 1.213 3.072 2.328 2.338 1.572 3.056 2.265 2.999 3.01-.01 1.215-.22 1.761-.972 2.327Z"
              />
            </svg>
          </StyledInviteTeammatesArrowDiv>
          <StyledOnboardingInboxInviteTeammatesVisualDiv>
            <OnboardingInboxInviteTeammatesVisual invitedTeammateHandles={invitedTeammateHandles ?? []} />
          </StyledOnboardingInboxInviteTeammatesVisualDiv>
        </>
      )}
    </StyledParentDiv>
  );
};

function mapPositionToPanAnimation(
  previousPosition: OnboardingInboxVisualInboxPosition | undefined,
  position: OnboardingInboxVisualInboxPosition | undefined,
) {
  if (
    previousPosition === OnboardingInboxVisualInboxPosition.LEFT &&
    position === OnboardingInboxVisualInboxPosition.CENTER
  ) {
    return PanAnimation.LEFT_CENTER;
  }

  if (
    previousPosition === OnboardingInboxVisualInboxPosition.CENTER &&
    position === OnboardingInboxVisualInboxPosition.RIGHT
  ) {
    return PanAnimation.CENTER_RIGHT;
  }

  return undefined;
}
